import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paymentsApi } from '../../api/payments';
import { useWallet, useTransactions } from '../../hooks/useWallet';
import { useUiStore } from '../../store/uiStore';
import { BalancePill } from '../../components/BalancePill';
import { Spinner } from '../../components/Spinner';
import type { TokenPackage, Transaction } from '../../types';

export function WalletPage() {
  const { balance } = useWallet();
  const pushToast = useUiStore((s) => s.pushToast);

  const packagesQuery = useQuery({
    queryKey: ['token-packages'],
    queryFn: () => paymentsApi.listPackages(),
  });

  const {
    data,
    isLoading: txLoading,
    isError: txError,
    error: txErr,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTransactions();

  const [selected, setSelected] = useState<TokenPackage | null>(null);
  const [proofUrl, setProofUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmitProof() {
    if (!selected) return;
    if (!proofUrl.trim()) {
      pushToast('error', 'Please paste a payment proof image URL.');
      return;
    }
    setSubmitting(true);
    try {
      await paymentsApi.submitProof(selected.id, proofUrl.trim());
      pushToast(
        'success',
        'Proof submitted — an admin will review it shortly.',
      );
      setSelected(null);
      setProofUrl('');
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Could not submit proof',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const transactions: Transaction[] =
    data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="space-y-8">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Wallet</h1>
        <BalancePill />
      </header>

      <section className="card space-y-2">
        <p className="label">Current balance</p>
        <p className="text-4xl font-bold text-gold">
          {balance === null ? '—' : balance.toLocaleString()}
          <span className="ml-2 text-base font-medium text-slate-400">
            tokens
          </span>
        </p>
      </section>

      {/* Buy tokens */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-100">Buy tokens</h2>

        {packagesQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : packagesQuery.isError ? (
          <p className="card text-sm text-rose-400">
            {packagesQuery.error instanceof Error
              ? packagesQuery.error.message
              : 'Failed to load packages.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(packagesQuery.data?.packages ?? []).map((pkg) => (
              <div key={pkg.id} className="card flex flex-col gap-2">
                <h3 className="text-lg font-semibold text-slate-100">
                  {pkg.name}
                </h3>
                <p className="text-gold">
                  {pkg.tokenAmount.toLocaleString()} tokens
                </p>
                <p className="text-sm text-slate-400">{pkg.priceDisplay}</p>
                <button
                  type="button"
                  className="btn-gold mt-auto"
                  onClick={() => {
                    setSelected(pkg);
                    setProofUrl('');
                  }}
                >
                  Buy
                </button>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div className="card space-y-3">
            <h3 className="text-lg font-semibold text-slate-100">
              Submit payment proof — {selected.name}
            </h3>
            <p className="text-xs text-slate-400">
              Complete your payment, then paste a link to a screenshot/photo of
              your payment proof below. An admin will review and credit your
              tokens. (Direct image upload is coming soon — for now, a URL is
              required.)
            </p>
            <div>
              <label className="label" htmlFor="proof-url">
                Payment proof image URL
              </label>
              <input
                id="proof-url"
                type="url"
                className="input"
                placeholder="https://…"
                value={proofUrl}
                onChange={(e) => setProofUrl(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-gold"
                onClick={handleSubmitProof}
                disabled={submitting}
              >
                {submitting ? <Spinner /> : 'Submit proof'}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setSelected(null)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Transaction history */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-100">
          Transaction history
        </h2>

        {txLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : txError ? (
          <p className="card text-sm text-rose-400">
            {txErr instanceof Error
              ? txErr.message
              : 'Failed to load transactions.'}
          </p>
        ) : transactions.length === 0 ? (
          <p className="card text-sm text-slate-400">No transactions yet.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {transactions.map((txn) => (
                <li
                  key={txn.id}
                  className="card flex flex-wrap items-center justify-between gap-2"
                >
                  <div>
                    <p className="font-semibold capitalize text-slate-100">
                      {txn.type}
                    </p>
                    <p className="text-xs text-slate-400">{txn.reason}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(txn.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-bold ${
                        txn.amount >= 0 ? 'text-emerald-400' : 'text-rose-400'
                      }`}
                    >
                      {txn.amount >= 0 ? '+' : ''}
                      {txn.amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-400">
                      Balance: {txn.balanceAfter.toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {hasNextPage && (
              <button
                type="button"
                className="btn-ghost w-full"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? <Spinner /> : 'Load more'}
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
